import type { SmartAccountWallet } from '@/lib/protection/smart-account';

/**
 * Lift a blunt server/auth error into something the holder can act on.
 *
 * `requireAuth` returns "Address does not belong to authenticated user"
 * verbatim, and the 7710 path's EIP-1271 fall-through surfaces
 * "Signature does not match holder" when the holder EOA was upgraded to
 * a Smart Account by another wallet. Both are accurate but useless to a
 * user; rewrite them into a recovery they can act on.
 *
 * Extracted from the (now-removed) Land Protection dialog so the
 * manage-rail footer can keep using it without the dialog.
 */
export function toFriendlyAuthError(
  message: string,
  wallet?: SmartAccountWallet | null,
): string {
  const lower = message.toLowerCase();
  if (
    lower.includes('does not belong to authenticated user') ||
    lower.includes('wallet_not_linked')
  ) {
    return 'Your connected wallet isn’t linked to your session. Sign in with this wallet and try again.';
  }
  if (
    lower.includes('wallet bound to a smart account') ||
    lower.includes('wallet-bound-to-sca') ||
    lower.includes('signature does not match holder')
  ) {
    if (wallet === 'dm-compatible') {
      return 'This address is already a smart account our keeper can top up, but it was upgraded by a different wallet than the one you’re using now — that’s a property of the address. Enable protection from the wallet that controls it, or connect a different address.';
    }
    return 'This address was upgraded to a smart account by another wallet — not the wallet you’re using now. Sign from the wallet that controls it, or connect a different address.';
  }
  return message;
}

/**
 * True when the auth error is "this wallet isn't linked to the current
 * session" — `requireAuth` rejecting because the connected wallet hasn't
 * signed in yet. Distinct from the other failures because the recovery is
 * to SIGN IN with this wallet, not to retry the same action. Matches the
 * same raw strings `toFriendlyAuthError` maps to the "not linked" copy.
 */
export function isWalletNotLinkedError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('does not belong to authenticated user') ||
    lower.includes('wallet_not_linked')
  );
}
